import { Card } from '@/components/ui/card';
import { Compass, Building2, Users, Mountain, Hotel, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

export const ServicesSection = () => {
  const services = [
    {
      icon: Compass,
      title: 'Custom Holiday Packages',
      description: 'Tailor-made travel experiences designed around your preferences, budget, and timeline. Local and international destinations.',
      color: 'primary'
    },
    {
      icon: Mountain,
      title: 'Safari & Wildlife Tours',
      description: 'Experience Africa\'s incredible wildlife up close with expertly guided safari tours across premier national parks.',
      color: 'accent'
    },
    {
      icon: Users,
      title: 'Cultural & Heritage',
      description: 'Immerse yourself in rich African cultures, traditions, and heritage sites with authentic local experiences.',
      color: 'secondary'
    },
    {
      icon: Building2,
      title: 'Corporate & Group Travel',
      description: 'Seamless travel management for corporate events, team building, conferences, and large group expeditions.',
      color: 'primary'
    },
    {
      icon: Hotel,
      title: 'Hotel Bookings',
      description: 'Access to exclusive accommodations from luxury lodges to boutique hotels, all carefully vetted for quality.',
      color: 'accent'
    },
    {
      icon: Zap,
      title: 'Adrenaline Activities',
      description: 'Bungee jumping, white-water rafting, zip-lining, and more thrilling adventures for the adventurous spirit.',
      color: 'secondary'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      primary: 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground',
      accent: 'bg-accent/10 text-accent group-hover:bg-accent group-hover:text-accent-foreground',
      secondary: 'bg-secondary/10 text-secondary-foreground group-hover:bg-secondary group-hover:text-secondary-foreground'
    };
    return colors[color] || colors.primary;
  };

  return (
    <section id="services" className="section-padding bg-gradient-to-b from-muted/30 to-background">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
            Our Services
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6 font-serif leading-tight">
            Comprehensive Travel Solutions
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            From custom safari adventures to corporate travel management, we offer a full spectrum of services to make your African journey seamless and unforgettable.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card
                key={index}
                className="group p-8 border-2 hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 bg-gradient-to-br from-card to-card/50"
              >
                {/* Icon */}
                <div className="mb-6">
                  <div className={`inline-flex w-14 h-14 rounded-xl items-center justify-center transition-all duration-300 ${
                    getColorClasses(service.color)
                  }`}>
                    <Icon className="w-7 h-7" />
                  </div>
                </div>

                {/* Content */}
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-3 font-serif group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-6 bg-primary/5 border-2 border-primary/20 rounded-2xl p-8">
            <div className="flex-1 text-left">
              <h3 className="text-2xl font-bold text-foreground mb-2 font-serif">
                Need a Custom Package?
              </h3>
              <p className="text-muted-foreground">
                Let us design the perfect African adventure tailored specifically to your dreams and budget.
              </p>
            </div>
            <Link
              to="/contact"
              className="px-8 py-3 bg-primary hover:bg-primary-dark text-primary-foreground font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap"
            >
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};